/**
 * /entity/<slug> — research entity page (manufacturer / ingredient /
 * disease / surveillance_system) when the slug resolves to a research
 * entity. Reuses ResearchPageScaffold so the Page Contract is identical.
 *
 * Falls back to the existing legacy EntityPage by re-exporting it for
 * non-research entity types. This file does NOT replace EntityPage —
 * it's mounted on a different route so the legacy page stays untouched.
 */
import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Syringe, Globe } from "lucide-react";
import api from "../../lib/api";
import ResearchPageScaffold from "../../components/research/ResearchPageScaffold";
import RelationshipGraph from "../../components/research/RelationshipGraph";
import ContinueExploring from "../../components/research/ContinueExploring";
import EvidenceBlock from "../../components/research/EvidenceBlock";
import { renderMarkdown } from "../../lib/markdown";

const TYPE_LABEL = {
    manufacturer: "Manufacturer",
    ingredient: "Ingredient",
    disease: "Disease",
    surveillance_system: "Surveillance system",
};

export default function ResearchEntityPage() {
    const { slug } = useParams();
    const [bundle, setBundle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setLoading(true);
        api.get(`/research-entities/${slug}/detail`)
            .then(({ data }) => setBundle(data))
            .catch((err) => { if (err?.response?.status === 404) setNotFound(true); })
            .finally(() => setLoading(false));
    }, [slug]);

    if (loading) return <div className="min-h-screen bg-[#0B1220] text-[#94A3B8] flex items-center justify-center">Loading…</div>;
    if (notFound || !bundle) return <Navigate to="/vaccines" replace />;

    const { entity, vaccines, relationships, related_entities, evidence_blocks } = bundle;
    const parentRef = `${entity.entity_type}:${entity.slug}`;
    const entitiesById = Object.fromEntries([entity, ...related_entities].map((e) => [e.id, e]));

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": entity.entity_type === "disease" ? "MedicalCondition"
            : entity.entity_type === "manufacturer" ? "Organization"
            : "Thing",
        name: entity.name,
        alternateName: entity.aka || [],
        description: entity.summary_md ? entity.summary_md.slice(0, 240) : `${TYPE_LABEL[entity.entity_type]} indexed by The Context Project.`,
        url: entity.official_url || undefined,
    };

    const continueItems = [
        ...vaccines.map((v) => ({ kind: "vaccine", slug: v.slug, title: v.display_name, badge: "vaccine" })),
        ...related_entities.map((e) => ({ kind: e.entity_type, slug: e.slug, title: e.name, badge: e.entity_type.replace("_", " ") })),
    ];

    return (
        <ResearchPageScaffold
            title={entity.name}
            description={entity.summary_md ? entity.summary_md.slice(0, 240) : `${TYPE_LABEL[entity.entity_type]} record`}
            canonicalPath={`/entity/${entity.slug}`}
            jsonLd={jsonLd}
            eyebrow={`${TYPE_LABEL[entity.entity_type]}${entity.country ? ` · ${entity.country}` : ""}`}
            breadcrumbs={[
                { label: "Library", href: "/" },
                { label: "Vaccines", href: "/vaccines" },
                { label: entity.name },
            ]}
            bookmark={{ kind: entity.entity_type, refSlug: entity.slug, refTitle: entity.name }}
            heroSlot={
                <div className="space-y-4">
                    {entity.summary_md ? (
                        <div className="prose prose-invert prose-sm max-w-3xl text-[#CBD5E1]" dangerouslySetInnerHTML={{ __html: renderMarkdown(entity.summary_md) }} />
                    ) : null}
                    {entity.official_url ? (
                        <a href={entity.official_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[12px] text-[#56CCF2] hover:text-white transition-colors">
                            <Globe className="w-3.5 h-3.5" />
                            {entity.official_url.replace(/^https?:\/\//, "")}
                        </a>
                    ) : null}
                </div>
            }
            connectedSlot={
                <div className="space-y-6">
                    {vaccines.length > 0 ? (
                        <section className="bg-[#111827] border border-[#243044] rounded-xl p-5" data-testid="entity-connected-vaccines">
                            <div className="flex items-center gap-2 mb-3">
                                <Syringe className="w-4 h-4 text-[#56CCF2]" />
                                <h3 className="text-[10px] uppercase tracking-[0.22em] text-[#94A3B8] font-semibold">
                                    {entity.entity_type === "manufacturer" ? "Vaccines manufactured" :
                                     entity.entity_type === "ingredient" ? "Vaccines containing this ingredient" :
                                     entity.entity_type === "disease" ? "Vaccines targeting this disease" :
                                     "Connected vaccines"}
                                </h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {vaccines.map((v) => (
                                    <Link key={v.id} to={`/vaccines/v/${v.slug}`} className="group flex items-center gap-2 bg-[#0B1220] border border-[#243044] hover:border-[#56CCF2]/50 rounded-md px-3 py-2 transition-colors">
                                        <Syringe className="w-3.5 h-3.5 text-[#56CCF2]" />
                                        <span className="text-sm text-white group-hover:text-[#56CCF2] transition-colors">{v.display_name}</span>
                                    </Link>
                                ))}
                            </div>
                        </section>
                    ) : null}
                    {relationships.length > 0 ? (
                        <RelationshipGraph focusEntity={entity} relationships={relationships} entitiesById={entitiesById} parentRef={parentRef} />
                    ) : null}
                </div>
            }
            evidenceSlot={
                evidence_blocks.length > 0 ? (
                    <div className="space-y-4">
                        <header>
                            <div className="text-[10px] uppercase tracking-[0.28em] text-[#56CCF2] font-semibold mb-1.5">Evidence</div>
                            <h2 className="text-2xl font-bold text-white tracking-tight">What is documented?</h2>
                        </header>
                        {evidence_blocks.map((b) => (
                            <EvidenceBlock key={b.id} block={b} parentRef={parentRef} />
                        ))}
                    </div>
                ) : null
            }
            continueSlot={continueItems.length > 0 ? <ContinueExploring items={continueItems} parentRef={parentRef} /> : null}
        />
    );
}
